import CabinetSwitchers from "../../../../../../../kvComponent/navigation/cabinetSwitchers";
import {SHAPE_PART_ID} from "../../../../../../constants";

/**
 * Bottom Cabinet wizard matching helpers.
 *
 * Public flow:
 * - getEquivalentBottomCabinetFeature: Top Cabinet feature -> equivalent Bottom Cabinet feature.
 * - getCurrentBottomCabinetWizardGroup: merged wizard step -> real profile/color group.
 * - getMatchingBottomWizardNavItem: current wizard group/navItems -> selected wizard feature.
 *
 * Renuit bottom cabinets are split into profile and color wizard steps, but
 * SQFT receives one concrete SKU. Matching therefore falls back to SKU metadata:
 * ActualDisplayedName[0] for profile/style and MatchingFeature or
 * ActualDisplayedName[1] for color.
 */

export const BOTTOM_CABINET_GROUP_NAME = "Bottom Cabinet";

const BOTTOM_CABINET_PROFILE_STEP_KEYWORDS = ["profile", "lineup", "style"];
const BOTTOM_CABINET_COLOR_STEP_KEYWORDS = ["colour", "color"];

const normalizeMatchText = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
};

const getFeatureMetadataValues = (feature, key) => {
  const value = feature?.metaData?.[key];

  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  return value ? [value] : [];
};

const textMatchesTerms = (text, terms) => {
  const normalizedText = normalizeMatchText(text);

  return terms.some(term => {
    const normalizedTerm = normalizeMatchText(term);
    return normalizedTerm.length > 2
      && (normalizedText.includes(normalizedTerm) || normalizedTerm.includes(normalizedText));
  });
};

export const getWizardSelectedFeaturesByPartId = (wizardSelectedValues, partId) => {
  if (!wizardSelectedValues || !partId) {
    return [];
  }

  return Object.keys(wizardSelectedValues)
    .map(stepIndex => parseInt(stepIndex, 10))
    .filter(Number.isInteger)
    .sort((a, b) => a - b)
    .reduce((features, stepIndex) => {
      const selectedFeature = wizardSelectedValues[stepIndex]?.selectedFeature;

      if (selectedFeature?.partId === partId) {
        features.push(selectedFeature);
      }

      return features;
    }, []);
};

export const hasWizardSelectedFeaturePartId = (wizardSelectedValues, partId) => {
  return getWizardSelectedFeaturesByPartId(wizardSelectedValues, partId).length > 0;
};

/**
 * Split cabinet flows can select the same partId in profile and color steps.
 * The last selected step contains the most specific feature for the completed path.
 */
export const getLastWizardSelectedFeatureByPartId = (wizardSelectedValues, partId) => {
  const selectedFeatures = getWizardSelectedFeaturesByPartId(wizardSelectedValues, partId);

  return selectedFeatures[selectedFeatures.length - 1] || null;
};

const getNormalizedFeatureWords = (feature) => {
  const getFeatureName = (feature) => {
    return feature?.getFeatureName?.() || feature?.partName || "";
  };

  return getFeatureName(feature)
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
};

const getCabinetLineupKey = (feature) => {
  const words = getNormalizedFeatureWords(feature);
  const numericLineup = words.find(word => /^\d+$/.test(word));

  if (numericLineup) {
    return numericLineup;
  }

  return words.slice(0, 2).join(" ");
};

const featureNameHasLineupKey = (feature, lineupKey) => {
  if (!lineupKey) {
    return false;
  }

  const words = getNormalizedFeatureWords(feature);

  if (/^\d+$/.test(lineupKey)) {
    return words.includes(lineupKey);
  }

  return words.join(" ").includes(lineupKey);
};

const getFeatureSharedName = (feature) => {
  return feature?.dbFeature?.sharedFeatureName;
};

const getCabinetSwitcherName = (feature) => {
  if (!feature) {
    return null;
  }

  const splittedFeatureName = feature.getSplittedFeatureName?.();

  return splittedFeatureName
    ? splittedFeatureName.replace(`${feature.dbFeature.groupName} - `, "")
    : null;
};

const getFeatureTextValues = (feature) => {
  if (!feature) {
    return [];
  }

  return [
    feature.partName,
    feature.dbFeature?.name,
    feature.dbFeature?.customDisplayName,
    getFeatureSharedName(feature),
    getCabinetSwitcherName(feature),
    feature.getFeatureName?.(),
    feature.getSplittedFeatureName?.(),
    ...getFeatureMetadataValues(feature, "ActualDisplayedName"),
    ...getFeatureMetadataValues(feature, "MatchingFeature"),
  ].filter(Boolean);
};

const featureMatchesTerms = (feature, terms) => {
  return getFeatureTextValues(feature).some(text => textMatchesTerms(text, terms));
};

const getBottomCabinetStyleTerms = (feature) => {
  const actualDisplayedName = getFeatureMetadataValues(feature, "ActualDisplayedName");

  return [
    actualDisplayedName[0],
    getCabinetSwitcherName(feature),
  ].filter(Boolean);
};

const getBottomCabinetColorTerms = (feature) => {
  const actualDisplayedName = getFeatureMetadataValues(feature, "ActualDisplayedName");

  return [
    ...getFeatureMetadataValues(feature, "MatchingFeature"),
    ...actualDisplayedName.slice(1),
  ].filter(Boolean);
};

const getWizardGroupTextValues = (wizardGroup) => {
  return [
    wizardGroup?.label,
    wizardGroup?.title,
    wizardGroup?.description,
    wizardGroup?.customDisplayName,
    ...(wizardGroup?.featuresIncluded || []).reduce((values, includedFeature) => {
      values.push(
        includedFeature?.name,
        includedFeature?.sharedFeatureName,
        includedFeature?.sku
      );
      return values;
    }, []),
  ].filter(Boolean);
};

const wizardGroupMatchesTerms = (wizardGroup, terms) => {
  return getWizardGroupTextValues(wizardGroup).some(text => textMatchesTerms(text, terms));
};

const wizardGroupTextIncludesAny = (wizardGroup, keywords) => {
  return keywords.some(keyword => {
    return getWizardGroupTextValues(wizardGroup).some(text => normalizeMatchText(text).includes(keyword));
  });
};

const getFeatureId = (feature) => {
  return feature?.dbFeature?.id;
};

const featureMatchesCandidate = (candidateFeature, feature) => {
  const featureId = getFeatureId(feature);
  const featureSharedName = getFeatureSharedName(feature);
  const candidateSharedName = getFeatureSharedName(candidateFeature);
  const featureSwitcherName = getCabinetSwitcherName(feature);
  const candidateSwitcherName = getCabinetSwitcherName(candidateFeature);

  return getFeatureId(candidateFeature) === featureId
    || (featureSharedName && candidateSharedName === featureSharedName)
    || (featureSwitcherName && candidateSwitcherName === featureSwitcherName);
};

const featureLineupMatchesCandidate = (candidateFeature, feature) => {
  return featureNameHasLineupKey(candidateFeature, getCabinetLineupKey(feature));
};

const getMatchingWizardNavItem = (navItems, feature, terms = []) => {
  const directMatch = navItems.find(navItem => featureMatchesCandidate(navItem.part, feature));

  if (directMatch) {
    return directMatch;
  }

  const colorOptionMatch = navItems.reduce((matchedNavItem, navItem) => {
    if (matchedNavItem) {
      return matchedNavItem;
    }

    const matchedColorOption = navItem.colorOptions?.find(option => {
      return featureMatchesCandidate(option.feature, feature);
    });

    return matchedColorOption
      ? Object.assign({}, navItem, {part: matchedColorOption.feature})
      : null;
  }, null);

  if (colorOptionMatch) {
    return colorOptionMatch;
  }

  if (terms.length) {
    const textMatch = navItems.find(navItem => featureMatchesTerms(navItem.part, terms));

    if (textMatch) {
      return textMatch;
    }

    const colorOptionTextMatch = navItems.reduce((matchedNavItem, navItem) => {
      if (matchedNavItem) {
        return matchedNavItem;
      }

      const matchedColorOption = navItem.colorOptions?.find(option => featureMatchesTerms(option.feature, terms));

      return matchedColorOption
        ? Object.assign({}, navItem, {part: matchedColorOption.feature})
        : null;
    }, null);

    if (colorOptionTextMatch) {
      return colorOptionTextMatch;
    }
  }

  return navItems.find(navItem => featureLineupMatchesCandidate(navItem.part, feature));
};

/**
 * SQFT cabinet shapes carry a concrete SKU, while the Renuit wizard selects
 * Bottom Cabinet through profile and color groups. These terms split the SKU
 * metadata into the pieces each wizard step can understand.
 */
const getBottomCabinetStepTerms = (wizardGroup, feature) => {
  if (wizardGroupTextIncludesAny(wizardGroup, BOTTOM_CABINET_COLOR_STEP_KEYWORDS)) {
    return getBottomCabinetColorTerms(feature);
  }

  if (wizardGroupTextIncludesAny(wizardGroup, BOTTOM_CABINET_PROFILE_STEP_KEYWORDS)) {
    return getBottomCabinetStyleTerms(feature);
  }

  return [];
};

/**
 * Converts a selected Top Cabinet feature to the matching Bottom Cabinet
 * feature. Bottom boxes can inherit the upper cabinet style when the lower
 * cabinet wizard step was skipped by the flow.
 */
export const getEquivalentBottomCabinetFeature = (layout, feature) => {
  const bottomCabinetFeatureGroup = layout.getFeatureGroupByPartId(SHAPE_PART_ID.bottom_cabinet);
  const cabinetSwitcherName = getCabinetSwitcherName(feature);

  if (bottomCabinetFeatureGroup && cabinetSwitcherName) {
    const switcherMatchedFeature = CabinetSwitchers.getNeededFeatureFromNavItems(
      cabinetSwitcherName,
      bottomCabinetFeatureGroup
    );

    if (switcherMatchedFeature) {
      return switcherMatchedFeature;
    }
  }

  const sharedFeatureName = getFeatureSharedName(feature);
  if (!sharedFeatureName) {
    return null;
  }

  return layout.getFeatureBySku(sharedFeatureName, SHAPE_PART_ID.bottom_cabinet, false, true);
};

/**
 * Picks the real Bottom Cabinet wizard group from merged/sibling groups.
 * Without this, the wizard can stay on the first available flow, like London
 * 900, and then fail to find colors for a Shaker Slim SKU.
 */
export const getCurrentBottomCabinetWizardGroup = (wizardStep, feature) => {
  const firstWizardGroup = wizardStep?.stepGroups?.[0];
  const wizardGroups = firstWizardGroup?.mergedGroups || wizardStep?.stepGroups || [];

  if (!wizardGroups.length) {
    return null;
  }

  const styleTerms = getBottomCabinetStyleTerms(feature);
  const colorTerms = getBottomCabinetColorTerms(feature);
  const matchingProfileGroup = wizardGroups.find(wizardGroup => {
    return wizardGroup.groupName === BOTTOM_CABINET_GROUP_NAME
      && wizardGroupTextIncludesAny(wizardGroup, BOTTOM_CABINET_PROFILE_STEP_KEYWORDS)
      && wizardGroupMatchesTerms(wizardGroup, styleTerms);
  });

  if (matchingProfileGroup) {
    return getCorrectedBottomCabinetWizardGroup(firstWizardGroup, matchingProfileGroup);
  }

  const matchingColorGroup = wizardGroups.find(wizardGroup => {
    return wizardGroup.groupName === BOTTOM_CABINET_GROUP_NAME
      && wizardGroupTextIncludesAny(wizardGroup, BOTTOM_CABINET_COLOR_STEP_KEYWORDS)
      && (wizardGroupMatchesTerms(wizardGroup, styleTerms) || wizardGroupMatchesTerms(wizardGroup, colorTerms));
  });

  return matchingColorGroup
    ? getCorrectedBottomCabinetWizardGroup(firstWizardGroup, matchingColorGroup)
    : firstWizardGroup;
};

const getCorrectedBottomCabinetWizardGroup = (firstWizardGroup, matchingWizardGroup) => {
  if (!firstWizardGroup?.mergedGroups) {
    return matchingWizardGroup;
  }

  return Object.assign({}, firstWizardGroup, {id: matchingWizardGroup.id});
};

/**
 * Finds the nav item that should be stored in wizardSelectedValues for the
 * current Bottom Cabinet step. Exact SKU/shared-name matches win; profile/color
 * text terms are only fallbacks for split Renuit wizard steps.
 */
export const getMatchingBottomWizardNavItem = (layout, navItems, wizardGroup, feature, selectedValues) => {
  const selectedTopCabinetFeatures = getWizardSelectedFeaturesByPartId(selectedValues, SHAPE_PART_ID.top_cabinet);
  const featuresToMatch = selectedTopCabinetFeatures.reduce((features, selectedFeature) => {
    const bottomFeature = getEquivalentBottomCabinetFeature(layout, selectedFeature);

    if (bottomFeature) {
      features.push(bottomFeature);
    }

    features.push(selectedFeature);
    return features;
  }, [feature]);

  return featuresToMatch.reduce((matchedNavItem, featureToMatch) => {
    return matchedNavItem || getMatchingWizardNavItem(
      navItems,
      featureToMatch,
      getBottomCabinetStepTerms(wizardGroup, featureToMatch)
    );
  }, null);
};
